import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { DEFAULT_CURRENCIES, CHARGE_CURRENCY, formatAmount, mergeRates } from "@/lib/currencies";
import { SUPPORTED_LANGS, contentLang, directionOf } from "@/lib/languages";
import { shellL } from "@/i18n/shell";

// Set the moment a visitor picks a language or a currency themselves: from
// then on the site never proposes another one.
const CHOSEN_KEY = "touki_prefs_chosen";
// Set once the opening proposal (from /api/geo) has been applied, so it
// happens on the first visit and not on every page. Renamed when Wolof came
// (2026-10-07), so a visitor in Senegal who never chose meets it once.
const GEO_KEY = "touki_geo_done_wo";
// The API client is loaded only when detection runs: every screen mounts this
// provider, and most tests mount it without wanting an HTTP client at all.
const client = () => import("@/lib/api").then((m) => m.default);

// The shell's words live in i18n/shell.js, in the six languages the site opens in.
const I18N = { ...shellL };

const SettingsContext = createContext(null);

// Under jest the provider stays quiet unless a test asks for the detection
// (detect={true}); every page test would otherwise see a /geo request.
const DETECT_BY_DEFAULT = process.env.NODE_ENV !== "test";

function readPreference(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

export function SettingsProvider({ children, detect = DETECT_BY_DEFAULT }) {
  // `siteLang` is the language the visitor chose (Wolof included); `lang` is the
  // language every word but the presentation texts is in (French under Wolof).
  const [siteLang, setLangState] = useState(() => readPreference("touki_lang") || "fr");
  const lang = contentLang(siteLang);
  const [currency, setCurrencyState] = useState(() => readPreference("touki_currency") || "XOF");
  const [currencies, setCurrencies] = useState(DEFAULT_CURRENCIES);
  const chosenHere = useRef(false);
  // A visitor's own pick outranks any proposal, now and on later visits.
  const setLang = useCallback((next) => { chosenHere.current = true; try { localStorage.setItem(CHOSEN_KEY, "1"); } catch { /* storage blocked */ } setLangState(next); }, []);
  const setCurrency = useCallback((next) => { chosenHere.current = true; try { localStorage.setItem(CHOSEN_KEY, "1"); } catch { /* storage blocked */ } setCurrencyState(next); }, []);

  // The backend's table replaces the defaults; a failure keeps the defaults.
  useEffect(() => {
    if (!detect) return undefined;
    let live = true;
    client().then((api) => api.get("/currency/rates")).then((r) => { if (live && r?.data) setCurrencies((t) => mergeRates(t, r.data)); }).catch(() => {});
    return () => { live = false; };
  }, [detect]);

  // First visit: open in the currency and language of where the visitor is,
  // the way Booking.com does. Never after they chose themselves.
  useEffect(() => {
    if (!detect) return undefined;
    let chosen = false, done = false;
    try { chosen = Boolean(localStorage.getItem(CHOSEN_KEY)); done = Boolean(localStorage.getItem(GEO_KEY)); } catch { /* storage blocked */ }
    if (chosen || done) return undefined;
    let live = true;
    client().then((api) => api.get("/geo")).then((r) => {
      const geo = r?.data;
      if (!live || !geo || chosenHere.current || readPreference(CHOSEN_KEY)) return;
      try { localStorage.setItem(GEO_KEY, "1"); } catch { /* storage blocked */ }
      // In Senegal the site opens in Wolof (owner, 2026-10-07).
      const nextLang = geo.country === "SN" ? "wo" : SUPPORTED_LANGS.includes(geo.lang) ? geo.lang : null;
      const nextCurrency = geo.currency && (DEFAULT_CURRENCIES[geo.currency] || currencies[geo.currency]) ? geo.currency : null;
      if (nextLang) setLangState(nextLang);
      if (nextCurrency) setCurrencyState(nextCurrency);
    }).catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detect]);
  useEffect(() => { try { localStorage.setItem("touki_lang", siteLang); } catch { /* use this visit's state */ } }, [siteLang]);
  useEffect(() => {
    document.documentElement.lang = SUPPORTED_LANGS.includes(lang) ? lang : "en";
    // Arabic reads right to left; the whole page turns with it.
    document.documentElement.dir = directionOf(lang);
  }, [lang]);
  useEffect(() => { try { localStorage.setItem("touki_currency", currency); } catch { /* use this visit's state */ } }, [currency]);

  const t = (path) => {
    const parts = path.split(".");
    let v = I18N[lang] || I18N.en;
    for (const p of parts) v = v?.[p];
    if (v == null) {
      // fallback to en
      let f = I18N.en;
      for (const p of parts) f = f?.[p];
      return f ?? path;
    }
    return v;
  };

  const formatPrice = (xof) => formatAmount(xof, currency, currencies, lang);
  // The same amount in another currency: what the debit will be, in USD.
  const formatIn = (xof, code) => formatAmount(xof, code, currencies, lang);

  return (
    <SettingsContext.Provider value={{ lang, siteLang, setLang, currency, setCurrency, t, formatPrice, formatIn, currencies, chargeCurrency: CHARGE_CURRENCY }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
